/**
 * Keep Hoxter diagram callouts connected to their image anchors as the layout
 * changes. This module is intentionally scoped to `.hoxter-ext` so the legacy
 * Premium extended-description interactions remain untouched.
 */
(function () {
    'use strict';

    const svgNamespace = 'http://www.w3.org/2000/svg';
    const connectorMedia = window.matchMedia('(min-width: 992px)');

    const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);
    const round = (value) => Math.round(value * 10) / 10;

    const getAnchor = (feature) => {
        const values = String(feature.dataset.hoxterAnchor || '').split(',').map(Number);

        if (values.length !== 2 || !values.every(Number.isFinite)) {
            return null;
        }

        return {
            x: clamp(values[0], 0, 100),
            y: clamp(values[1], 0, 100)
        };
    };

    const toLocalRect = (element, containerRect, scaleX, scaleY) => {
        const rect = element.getBoundingClientRect();
        const left = (rect.left - containerRect.left) * scaleX;
        const top = (rect.top - containerRect.top) * scaleY;
        const width = rect.width * scaleX;
        const height = rect.height * scaleY;

        return {
            left,
            top,
            width,
            height,
            right: left + width,
            bottom: top + height
        };
    };

    const getFeaturePort = (rect, target) => {
        const insetX = Math.min(12, rect.width / 2);
        const insetY = Math.min(12, rect.height / 2);
        const candidates = [
            {
                side: 'left',
                x: rect.left,
                y: clamp(target.y, rect.top + insetY, rect.bottom - insetY)
            },
            {
                side: 'right',
                x: rect.right,
                y: clamp(target.y, rect.top + insetY, rect.bottom - insetY)
            },
            {
                side: 'top',
                x: clamp(target.x, rect.left + insetX, rect.right - insetX),
                y: rect.top
            },
            {
                side: 'bottom',
                x: clamp(target.x, rect.left + insetX, rect.right - insetX),
                y: rect.bottom
            }
        ];

        return candidates.reduce((closest, candidate) => {
            const distance = Math.pow(candidate.x - target.x, 2) + Math.pow(candidate.y - target.y, 2);

            return !closest || distance < closest.distance
                ? { ...candidate, distance }
                : closest;
        }, null);
    };

    const getConnectorPath = (port, target) => {
        if ('left' === port.side || 'right' === port.side) {
            const bendX = port.x + ((target.x - port.x) * 0.5);

            return [
                `M ${round(port.x)} ${round(port.y)}`,
                `H ${round(bendX)}`,
                `V ${round(target.y)}`,
                `H ${round(target.x)}`
            ].join(' ');
        }

        const bendY = port.y + ((target.y - port.y) * 0.5);

        return [
            `M ${round(port.x)} ${round(port.y)}`,
            `V ${round(bendY)}`,
            `H ${round(target.x)}`,
            `V ${round(target.y)}`
        ].join(' ');
    };

    const initialize = () => {
        const roots = Array.from(document.querySelectorAll('.hoxter-ext'));

        if (!roots.length) {
            return;
        }

        const diagrams = [];
        let animationFrame = 0;

        roots.forEach((root) => {
            root.querySelectorAll('[data-hoxter-diagram]').forEach((container) => {
                const image = container.querySelector('[data-hoxter-image]');
                const features = Array.from(container.querySelectorAll('[data-hoxter-anchor]'))
                    .map((feature) => ({
                        anchor: getAnchor(feature),
                        element: feature
                    }))
                    .filter((feature) => feature.anchor);

                if (!image || !features.length) {
                    return;
                }

                const svg = document.createElementNS(svgNamespace, 'svg');
                svg.classList.add('hoxter-ext__connectors');
                svg.setAttribute('aria-hidden', 'true');
                svg.setAttribute('focusable', 'false');
                svg.setAttribute('width', '100%');
                svg.setAttribute('height', '100%');
                svg.setAttribute('preserveAspectRatio', 'none');
                Object.assign(svg.style, {
                    display: 'none',
                    height: '100%',
                    inset: '0',
                    overflow: 'visible',
                    pointerEvents: 'none',
                    position: 'absolute',
                    width: '100%',
                    zIndex: '2'
                });

                features.forEach((feature) => {
                    const path = document.createElementNS(svgNamespace, 'path');
                    path.classList.add('hoxter-ext__connector');
                    path.setAttribute('fill', 'none');
                    path.setAttribute('stroke', '#c56b45');
                    path.setAttribute('stroke-width', '2');
                    path.setAttribute('stroke-dasharray', '6 5');
                    path.setAttribute('stroke-linecap', 'square');
                    path.setAttribute('stroke-linejoin', 'miter');
                    path.setAttribute('vector-effect', 'non-scaling-stroke');
                    svg.appendChild(path);
                    feature.path = path;
                });

                container.appendChild(svg);
                diagrams.push({ container, features, image, svg });
            });
        });

        if (!diagrams.length) {
            return;
        }

        const renderDiagram = (diagram) => {
            const { container, features, image, svg } = diagram;

            if (!connectorMedia.matches || !container.isConnected || !container.offsetParent) {
                svg.style.display = 'none';
                container.removeAttribute('data-hoxter-connectors-active');
                return;
            }

            const containerRect = container.getBoundingClientRect();
            const width = container.clientWidth;
            const height = container.clientHeight;

            if (!width || !height || !containerRect.width || !containerRect.height) {
                svg.style.display = 'none';
                container.removeAttribute('data-hoxter-connectors-active');
                return;
            }

            const scaleX = width / containerRect.width;
            const scaleY = height / containerRect.height;
            const imageRect = toLocalRect(image, containerRect, scaleX, scaleY);

            if (!imageRect.width || !imageRect.height) {
                svg.style.display = 'none';
                container.removeAttribute('data-hoxter-connectors-active');
                return;
            }

            svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
            let renderedPaths = 0;

            features.forEach((feature) => {
                const featureRect = toLocalRect(feature.element, containerRect, scaleX, scaleY);

                if (!featureRect.width || !featureRect.height || !feature.element.offsetParent) {
                    feature.path.removeAttribute('d');
                    return;
                }

                const target = {
                    x: imageRect.left + ((feature.anchor.x / 100) * imageRect.width),
                    y: imageRect.top + ((feature.anchor.y / 100) * imageRect.height)
                };
                const port = getFeaturePort(featureRect, target);

                feature.path.setAttribute('d', getConnectorPath(port, target));
                renderedPaths += 1;
            });

            if (renderedPaths) {
                svg.style.display = 'block';
                container.setAttribute('data-hoxter-connectors-active', '');
            } else {
                svg.style.display = 'none';
                container.removeAttribute('data-hoxter-connectors-active');
            }
        };

        const render = () => {
            animationFrame = 0;
            diagrams.forEach(renderDiagram);
        };

        const scheduleRender = () => {
            if (animationFrame) {
                return;
            }

            animationFrame = window.requestAnimationFrame(render);
        };

        if ('ResizeObserver' in window) {
            const resizeObserver = new ResizeObserver(scheduleRender);

            diagrams.forEach((diagram) => {
                resizeObserver.observe(diagram.container);
                resizeObserver.observe(diagram.image);
                diagram.features.forEach((feature) => resizeObserver.observe(feature.element));
            });
        }

        diagrams.forEach((diagram) => {
            if (!diagram.image.complete) {
                diagram.image.addEventListener('load', scheduleRender, { once: true });
            }
        });

        roots.forEach((root) => {
            root.addEventListener('transitionend', scheduleRender);
        });

        window.addEventListener('load', scheduleRender, { once: true });
        window.addEventListener('resize', scheduleRender, { passive: true });
        window.addEventListener('orientationchange', scheduleRender, { passive: true });
        document.addEventListener('aos:in', scheduleRender);

        if ('addEventListener' in connectorMedia) {
            connectorMedia.addEventListener('change', scheduleRender);
        } else {
            connectorMedia.addListener(scheduleRender);
        }

        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(scheduleRender);
        }

        scheduleRender();
    };

    if ('loading' === document.readyState) {
        document.addEventListener('DOMContentLoaded', initialize, { once: true });
    } else {
        initialize();
    }
})();
